// Thông báo cá nhân (notification-service ListNotifications / GetUnreadNotificationCount — đặc tả notification §6.3, §9,
// Phụ lục A): cọc được duyệt / bị từ chối, hợp đồng đã ký, căn bị mở khoá, kỳ chi trả… Hiển thị ở tab "Thông báo" trong
// menu chuông; số chưa đọc cộng vào chấm số của chuông. Kiểu khai báo ở libs/contracts/src/notification (bên sở hữu).
import { useQuery } from '@tanstack/react-query';
import type { notification } from '@cathouse/contracts';
import { ApiError, http, qk, type RealtimeBinding } from '@/shared/api';
import { cx, fmtAgo, useServerNow } from '@/shared/lib';
import { Icon, type IconName } from '@/shared/ui';

export type NotificationItem = notification.NotificationItem;
export type NotificationList = notification.NotificationList;
export type NotificationSeverity = notification.NotificationSeverity;

/** Số thông báo mỗi lần tải trong menu chuông. */
export const NOTIFICATION_LIMIT = 30;
export const NOTIFICATION_POLL_MS = 60_000;
const MISSING_POLL_MS = 5 * 60_000;
/** Route chưa có (notification-service chưa nối vào gateway) ⇒ hỏi lại thưa, không hỏi khi focus; tin SSE vẫn làm mới. */
const missing = (e: unknown) => e instanceof ApiError && e.status === 404;

/** Danh sách thông báo mới nhất (đã đọc + chưa đọc); `enabled` = false khi menu chuông đóng. */
export const useNotifications = (enabled = true) =>
  useQuery({
    queryKey: qk.notifications.list({ limit: NOTIFICATION_LIMIT }),
    queryFn: () => http.get<NotificationList>('/notifications', { query: { limit: NOTIFICATION_LIMIT } }),
    enabled,
  });

/** Số thông báo chưa đọc (chấm số của chuông). Lỗi / route chưa có ⇒ 0. */
export const useUnreadCount = () => {
  const q = useQuery({
    queryKey: qk.notifications.unread(),
    queryFn: () => http.get<notification.UnreadNotificationCount>('/notifications/unread-count'),
    refetchInterval: (s) => (missing(s.state.error) ? MISSING_POLL_MS : NOTIFICATION_POLL_MS),
    refetchOnWindowFocus: (s) => !missing(s.state.error),
  });
  return q.data?.unread ?? 0;
};

/** Tin SSE 'notifications' (chỉ người nhận) ⇒ làm mới danh sách + số chưa đọc. */
export const notificationsRealtime: RealtimeBinding = { topic: 'notifications', module: 'dashboard', keys: () => [qk.notifications.all] };

/** Biểu tượng theo context của loại thông báo (deal.* ⇒ hồ sơ giao dịch, booking.* ⇒ bảng hàng…). */
const ICON_BY_CONTEXT: Record<string, IconName> = {
  deal: 'file',
  booking: 'grid',
  crm: 'users',
  attendance: 'clock',
  commission: 'wallet',
  inventory: 'building',
  hr: 'staff',
  security: 'shield',
};
export const notificationIcon = (kind: string): IconName => ICON_BY_CONTEXT[kind.split('.')[0] ?? ''] ?? 'bell';

/** Đồng hồ máy chủ cho cột "x phút trước" của danh sách thông báo (một bộ đếm cho cả danh sách). */
export const useNotificationClock = () => useServerNow(30_000);

/** Một dòng thông báo trong menu chuông (.alert-row): chưa đọc ⇒ chữ đậm + chấm; bấm ⇒ onOpen (đóng menu). */
export function NotificationRow({ item, now, onOpen }: { item: NotificationItem; now: number; onOpen?: (item: NotificationItem) => void }) {
  const unread = !item.readAt;
  return (
    <button type="button" className={cx('alert-row', 'notif-row', unread && 'unread')} data-close onClick={() => onOpen?.(item)}>
      <span className={`sev ${item.severity}`}>
        <Icon n={notificationIcon(item.kind)} s={15} />
      </span>
      <span className="grow">
        <span className="t">{item.title}</span>
        {item.body && <span className="s">{item.body}</span>}
        <span className="s">
          {fmtAgo(item.createdAt, now)}
          {unread && <span className="sr-only"> · chưa đọc</span>}
        </span>
      </span>
      {unread && <span className="unread-dot" aria-hidden="true" />}
    </button>
  );
}
